import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { toPersianDigits } from '../lib/date'
import { useField } from './Field'

/* ------------------------------------------------------------------
   انتخابگر ساعت ۲۴ ساعته
   ------------------------------------------------------------------
   🔴 چرا این کامپوننت ساخته شد:

   فرم‌ها از `<input type="time">` استفاده می‌کردند و کاربر AM/PM
   می‌دید. این ویجتِ بومیِ مرورگر است و فرمت ۱۲ یا ۲۴ ساعته‌اش را از
   **زبانِ رابطِ خودِ مرورگر** می‌گیرد — نه از `lang="fa"` روی صفحه،
   نه از `lang` روی خودِ input، و نه از هیچ ویژگی HTML دیگری.

   ⚠️ یعنی این با CSS یا attribute اصلاً قابل حل نبود: کاربری با
   مرورگر انگلیسی همیشه AM/PM می‌دید و کاربری با مرورگر فارسی نمی‌دید.
   تنها راهِ قطعی، نساختن به ویجتِ بومی است.

   ⚠️ همان مسیری که `JalaliDatePicker` رفت: آنجا هم `<input type="date">`
   تقویم میلادیِ مرورگر را تحمیل می‌کرد و با جایگزینی حل شد.

   ------------------------------------------------------------------
   ⚠️ مقدار ورودی و خروجی همیشه **`HH:MM` لاتین و ۲۴ ساعته** است.

   یعنی هیچ چیزی در Backend یا بقیهٔ فرم عوض نمی‌شود؛ فقط آنچه کاربر
   می‌بیند ارقام فارسی است. اگر مقدار هم فارسی می‌شد، هر مصرف‌کننده
   باید ارقام را قبل از ارسال برمی‌گرداند — منبع بی‌پایان اشتباه.

   ------------------------------------------------------------------
   🔴 دو ستونِ اسکرول‌شونده (ساعت و دقیقه) و نه یک فهرست بلند.

   یک فهرست از تمام ترکیب‌های ساعت:دقیقه با گامِ ۵ دقیقه، ۲۸۸ سطر
   می‌شود. با دو ستون، ۲۴ + ۱۲ سطر است و کاربر همیشه هر دو را می‌بیند.
   ------------------------------------------------------------------ */

/**
 * گامِ دقیقه — هر ۵ دقیقه.
 *
 * ⚠️ گامِ ۱ دقیقه‌ای ۶۰ سطر می‌ساخت و اسکرولِ ستونِ دقیقه را به اندازهٔ
 * ستونِ ساعت طولانی می‌کرد، بی‌آنکه کسی در ثبتِ مرخصی به دقتِ یک دقیقه
 * نیاز داشته باشد. مقادیرِ خارج از گام (مثلاً `09:07` که از Backend
 * آمده) همچنان نمایش داده می‌شوند — فقط در فهرست انتخاب نیستند.
 */
const MINUTE_STEP = 5

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP)

/** `HH:MM` را به عدد می‌شکند — و اگر معتبر نبود `null` می‌دهد. */
function parseTime(value: string | null): { h: number; m: number } | null {
  if (!value) return null

  const match = /^(\d{1,2}):(\d{1,2})$/.exec(value.trim())

  if (!match) return null

  const h = Number(match[1])
  const m = Number(match[2])

  if (h < 0 || h > 23 || m < 0 || m > 59) return null

  return { h, m }
}

/** عدد را دو رقمی و لاتین می‌کند — همان چیزی که به `onChange` می‌رود. */
const pad = (n: number) => String(n).padStart(2, '0')

export interface TimePickerProps {
  /** `HH:MM` ۲۴ ساعته — یا `null`/`''` وقتی خالی است */
  value: string | null
  /** همیشه `HH:MM` لاتینِ ۲۴ ساعته می‌دهد */
  onChange: (value: string) => void
  placeholder?: string
  /** عنوان بالای پنجره — وقتی چند ساعت در یک فرم هست، تفکیک‌کننده است */
  title?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
}

export function TimePicker({
  value,
  onChange,
  placeholder = 'انتخاب ساعت',
  title = 'انتخاب ساعت',
  disabled,
  invalid,
  className,
  id,
}: TimePickerProps) {
  const field = useField()
  const isInvalid = invalid ?? field?.invalid ?? false

  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const hourRef = useRef<HTMLButtonElement>(null)
  const minuteRef = useRef<HTMLButtonElement>(null)

  const selected = useMemo(() => parseTime(value), [value])

  /*
   | 🔴 وقتی هنوز چیزی انتخاب نشده، کلیک روی «۱۴» باید همان‌جا `14:00`
   | بدهد و نه اینکه منتظرِ انتخابِ دقیقه بماند.
   |
   | ⚠️ پس ساعت و دقیقه هر کدام جداگانه روی مقدارِ فعلی اعمال می‌شوند و
   | نیمهٔ دیگر از `selected` می‌آید (یا صفر، اگر خالی بوده). بدون این،
   | انتخابگر یک حالتِ «نیمه‌تمام» داشت که در `value` دیده نمی‌شد.
   */
  const commit = (h: number, m: number) => {
    onChange(`${pad(h)}:${pad(m)}`)
  }

  useEffect(() => {
    if (!open) return

    /*
     | ⚠️ Escape با `capture` گرفته می‌شود تا اگر انتخابگر داخل یک Dialog
     | باز شده باشد، اول این ببندد و نه پنجرهٔ بیرونی — وگرنه کاربر با
     | یک Escape کل فرمش را از دست می‌دهد.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
      }
    }

    /* اسکرول پس‌زمینه قفل می‌شود تا پنجرهٔ شناور با صفحه جابه‌جا نشود. */
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    const focusTimer = window.setTimeout(() => panelRef.current?.focus(), 0)

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = overflow

      /* فوکوس به همان دکمه‌ای برمی‌گردد که انتخابگر را باز کرده بود. */
      triggerRef.current?.focus()
    }
  }, [open])

  /*
   | هر دو ستون روی مقدارِ فعلی باز می‌شوند.
   |
   | ⚠️ `block: 'center'` و نه `nearest`: ستونِ ساعت ۲۴ سطر است و در
   | کادری با ارتفاعِ ~۷ سطر اسکرول می‌شود. بدون این، کاربری که `18:30`
   | دارد ابتدای فهرست (`00`) را می‌بیند و فکر می‌کند انتخابش پاک شده.
   */
  useEffect(() => {
    if (!open) return

    hourRef.current?.scrollIntoView({ block: 'center' })
    minuteRef.current?.scrollIntoView({ block: 'center' })
  }, [open])

  const label = selected
    ? `${toPersianDigits(pad(selected.h))}:${toPersianDigits(pad(selected.m))}`
    : null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id ?? field?.id}
        disabled={disabled}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field?.describedBy}
        /*
          ⚠️ همان مدل «Filled» بقیهٔ کنترل‌ها — زمینهٔ ملایم و خط زیرین.
          عمداً دستی تکرار می‌شود و از `controlBase` نمی‌آید: آن تابع
          صادر نشده و صادرکردنش یعنی یک API عمومیِ تازه در پکیج برای
          چیزی که فقط چند مصرف‌کننده دارد. ولی هر تغییری آنجا باید
          اینجا هم بیاید، وگرنه انتخابگر از بقیهٔ فرم جدا می‌افتد.
        */
        className={cn(
          'flex h-9.5 w-full items-center justify-between gap-2',
          /* ⚠️ سه طرف صریح شفاف — با `border-transparent` سراسری، خطِ
             زیرین به‌خاطر ترتیبِ خروجی Tailwind رنگ نمی‌گرفت. */
          'rounded-t-[var(--pf-radius)] rounded-b-[var(--pf-radius-sm)] px-3',
          'border border-x-transparent border-t-transparent',
          'text-start text-[length:var(--pf-text-md)]',
          'transition-[background-color,border-color] duration-[var(--pf-duration-fast)]',
          isInvalid
            ? 'bg-[var(--pf-danger-50)] border-b-2 border-b-[var(--pf-danger-500)]'
            : cn(
                'bg-[var(--pf-surface-muted)] border-b-2 border-b-[var(--pf-text-subtle)]',
                !disabled && 'hover:bg-[var(--pf-surface-hover)]',
                'focus-visible:border-b-[var(--pf-primary-container)]',
                /* پنجرهٔ باز = فیلدِ فعال. */
                open && 'border-b-[var(--pf-primary-container)]',
              ),
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
      >
        <span
          className={cn(
            'truncate',
            label ? 'text-[var(--pf-text)]' : 'text-[var(--pf-text-subtle)]',
          )}
        >
          {label ?? placeholder}
        </span>

        {/* ساعت — عقربه‌های ۱۰:۲، همان وزنِ خطِ آیکون‌های دیگر */}
        <svg
          className="size-4 shrink-0 text-[var(--pf-text-muted)]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      </button>

      {open &&
        createPortal(
          /*
            ⚠️ در موبایل `items-end` و در دسکتاپ `items-center` — کارت در
            موبایل از لبهٔ پایین بالا می‌آید و تمام‌عرض می‌شود، چون
            رساندن انگشت به وسط صفحه سخت‌تر از لبهٔ پایین است.
          */
          <div
            className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4"
            style={{ zIndex: 'var(--pf-z-dialog)' }}
          >
            {/*
              ⚠️ رنگ و شفافیت و بلور هر سه از توکن می‌آیند و نه از
              کلاس‌های ثابت. `color-mix` به جای نحو `/40` تیلویند به‌کار
              رفته چون آن نحو عدد ثابت می‌خواهد و نمی‌تواند درصد را از
              متغیر بخواند — و درصد باید در پوستهٔ تاریک فرق کند.
            */}
            <div
              className="absolute inset-0"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--pf-scrim) var(--pf-scrim-opacity), transparent)',
                backdropFilter: 'blur(var(--pf-scrim-blur))',
                WebkitBackdropFilter: 'blur(var(--pf-scrim-blur))',
              }}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />

            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label={title}
              tabIndex={-1}
              className={cn(
                'relative flex w-full flex-col overflow-hidden sm:max-w-[20rem]',
                'bg-[var(--pf-surface)] shadow-[var(--pf-shadow-xl)] focus:outline-none',
                'rounded-t-[var(--pf-radius-lg)] sm:rounded-[var(--pf-radius-lg)]',
              )}
            >
              <div className="shrink-0 border-b border-[var(--pf-border)] px-5 py-4">
                <h2 className="text-[length:var(--pf-text-lg)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]">
                  {title}
                </h2>
              </div>

              {/*
                ⚠️ `dir="ltr"` روی خودِ شبکه — ساعت باید سمتِ چپِ دقیقه
                بماند حتی در صفحهٔ راست‌چین، چون `۱۴:۳۰` همیشه
                ساعت‌سپس‌دقیقه خوانده می‌شود.
              */}
              <div dir="ltr" className="grid grid-cols-2 divide-x divide-[var(--pf-border)]">
                <TimeColumn
                  heading="ساعت"
                  values={HOURS}
                  selectedValue={selected?.h ?? null}
                  scrollRef={hourRef}
                  onSelect={(h) => commit(h, selected?.m ?? 0)}
                />

                <TimeColumn
                  heading="دقیقه"
                  values={MINUTES}
                  selectedValue={selected?.m ?? null}
                  scrollRef={minuteRef}
                  onSelect={(m) => commit(selected?.h ?? 0, m)}
                />
              </div>

              {/*
                🔴 بدون «تأیید»: هر کلیک همان‌جا در `value` ثبت می‌شود.

                ⚠️ ولی برخلاف تقویم، پنجره با انتخاب **بسته نمی‌شود** —
                چون انتخابِ کامل دو کلیک است (ساعت و دقیقه) و بستن با
                کلیکِ اول، کاربر را برای دقیقه مجبور به بازکردنِ دوباره
                می‌کرد. بستن با «تمام»، کلیکِ بیرون یا Escape است.
              */}
              <div className="flex shrink-0 items-center justify-end border-t border-[var(--pf-border)] px-5 py-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'rounded-full px-4 py-1.5 text-[length:var(--pf-text-sm)]',
                    'font-[var(--pf-weight-medium)] text-[var(--pf-text-muted)]',
                    'transition-colors hover:bg-[var(--pf-surface-hover)]',
                  )}
                >
                  تمام
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

/**
 * یک ستونِ اسکرول‌شونده — ساعت یا دقیقه.
 *
 * ⚠️ ارتفاع ثابت است و نه `max-h`: ستونِ دقیقه ۱۲ سطر دارد و ستونِ ساعت
 * ۲۴ سطر. با `max-h` ستونِ کوتاه‌تر کوتاه‌تر می‌ماند و کفِ کارت پله
 * می‌خورد.
 */
function TimeColumn({
  heading,
  values,
  selectedValue,
  scrollRef,
  onSelect,
}: {
  heading: string
  values: number[]
  selectedValue: number | null
  scrollRef: React.RefObject<HTMLButtonElement | null>
  onSelect: (value: number) => void
}) {
  return (
    <div className="flex flex-col">
      <div
        dir="rtl"
        className={cn(
          'shrink-0 px-3 py-2 text-center',
          'text-[length:var(--pf-text-xs)] font-[var(--pf-weight-medium)]',
          'text-[var(--pf-text-muted)]',
        )}
      >
        {heading}
      </div>

      <div className="h-[14rem] overflow-y-auto px-2 pb-2">
        {values.map((n) => {
          const isSelected = n === selectedValue

          return (
            <button
              key={n}
              ref={isSelected ? scrollRef : undefined}
              type="button"
              onClick={() => onSelect(n)}
              aria-pressed={isSelected}
              className={cn(
                'block w-full rounded-[var(--pf-radius-sm)] py-2 text-center',
                'text-[length:var(--pf-text-md)] tabular-nums',
                'transition-colors duration-[var(--pf-duration-fast)]',
                isSelected
                  ? /* ⚠️ همان جفتِ رنگی تقویم — روی `--pf-primary-container`
                       متنِ سفید کنتراستِ ۲٫۶۴ می‌داد که ناخواناست. */
                    cn(
                      'bg-[var(--pf-primary-container)]',
                      'text-[var(--pf-on-primary-container)]',
                      'font-[var(--pf-weight-semibold)]',
                    )
                  : 'text-[var(--pf-text)] hover:bg-[var(--pf-surface-hover)]',
              )}
            >
              {toPersianDigits(pad(n))}
            </button>
          )
        })}
      </div>
    </div>
  )
}
